import React, { ForwardedRef, forwardRef, PropsWithChildren } from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import Loading from '@/src/app/components/LoadingSpinner/LoadingSpinner';
import Icon from '@/src/app/components/Icon/Icon';
import { IconName } from '@/src/app/components/Icon/types';

export enum ButtonSize {
  LARGE = 'lg',
  MEDIUM = 'md',
  SMALL = 'sm',
}

type ButtonProps = PropsWithChildren<{
  primary?: boolean;
  priority?: boolean;
  outline?: boolean;
  text?: boolean;
  icon?: IconName;
  size?: ButtonSize;
  loading?: boolean;
  href?: string;
  target?: string;
  textClassName?: string;
}> &
  React.JSX.IntrinsicElements['button'] &
  React.JSX.IntrinsicElements['link'];

/**
 * Primary UI component for user interaction
 */
const Button = forwardRef(
  (
    {
      primary,
      priority,
      size,
      outline,
      loading,
      href,
      icon,
      children,
      className,
      disabled,
      text,
      textClassName,
      ...props
    }: PropsWithChildren<ButtonProps>,
    _: ForwardedRef<unknown>,
  ) => {
    const classes: Array<string> = className ? className.split(' ') : [];

    if (text) {
      classes.push('text');
    }
    if (outline) {
      classes.push('ghost');
    }
    if (primary) {
      classes.push('primary');
    }
    if (priority) {
      classes.push('priority');
    }
    if (size) {
      classes.push(size);
    }
    if (!children) {
      classes.push('square');
    }

    if (href) {
      return (
        // @ts-ignore
        <Link
          href={href}
          className={classNames('button no-underline!', classes)}
          rel={props.target === '_blank' ? 'nofollow,noopener' : undefined}
          {...props}
        >
          {icon ? <Icon name={icon} className="icon" /> : null}
          {children ? <span className={textClassName}>{children}</span> : null}
        </Link>
      );
    }

    return (
      <button
        type={props.type ? props.type : 'button'}
        className={classNames('button w-fit', classes)}
        disabled={disabled || loading}
        data-testid="button"
        {...props}
      >
        {loading ? (
          <Loading data-testid="spinner" className="spinner" />
        ) : icon ? (
          <Icon name={icon} className="icon" />
        ) : null}
        {children ? <span>{children}</span> : null}
      </button>
    );
  },
);

Button.displayName = 'ButtonComponent';

export default Button;
